"use client";
import React, { FC } from "react";
import { PortableText } from "@portabletext/react";
import styles from "./contactHeroSection.module.css";

export type ContactHeroSectionProps = {
  title?: string;
  description?: any; // Portable text array
};

const defaultTitle = "Tell me what you need.";
const defaultDescription = [
  {
    _type: "block",
    children: [
      {
        _type: "span",
        text: "Contract platform work, a website for your business, or something else.",
      },
    ],
  },
];

const staleTitles = new Set([
  "Tell me what your team needs.",
  "Ready to Transform Your team?",
  "Engineering Leadership Consulting",
]);

function descriptionText(description: ContactHeroSectionProps["description"]): string {
  if (!Array.isArray(description)) return "";
  return description
    .map((block) => {
      const children = Array.isArray(block?.children) ? block.children : [];
      return children.map((child: { text?: string }) => child?.text || "").join("");
    })
    .join(" ");
}

const ContactHeroSection: FC<ContactHeroSectionProps> = ({
  title = defaultTitle,
  description = defaultDescription,
}) => {
  const headline = !title || staleTitles.has(title) ? defaultTitle : title;
  const descriptionIsStale = descriptionText(description).includes(
    "Whether you need consulting for your engineering team",
  );
  const heroDescription = descriptionIsStale ? defaultDescription : description;
  return (
    <section
      className="w-full pt-6 pb-0"
      aria-label="Contact hero section"
    >
      <div className="mx-auto max-w-7xl pt-6 px-6 lg:px-8 text-center">
        <h1
          className={`mx-auto font-assistant font-semibold leading-tight text-4xl md:text-5xl lg:text-6xl max-w-3xl mb-8 ${styles.heroTitle} ${styles.heroTitleGradient}`}
        >
          {headline}
        </h1>
        <div
          className={`mx-auto font-inter font-normal text-slate-300 text-lg leading-7 max-w-3xl ${styles.heroDescription}`}
        >
          {heroDescription && Array.isArray(heroDescription) && heroDescription.length > 0 ? (
            <PortableText
              value={heroDescription}
              components={{
                block: {
                  normal: ({ children }) => <p>{children}</p>,
                },
                marks: {
                  strong: ({ children }) => (
                    <strong className="font-semibold">{children}</strong>
                  ),
                  em: ({ children }) => (
                    <em className="italic">{children}</em>
                  ),
                  link: ({ value, children }) => {
                    const target = value?.blank ? "_blank" : undefined;
                    const rel = value?.blank ? "noopener noreferrer" : undefined;
                    return (
                      <a
                        href={value?.href}
                        target={target}
                        rel={rel}
                        className="text-[#a78bfa] hover:text-[#c4b5fd] underline decoration-[#a78bfa]/50 hover:decoration-[#a78bfa] transition-colors duration-200 ease-in-out font-medium"
                      >
                        {children}
                      </a>
                    );
                  },
                },
              }}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
};

export default ContactHeroSection;
